import { Box, useBreakpointValue } from '@chakra-ui/react';
import * as React from 'react';

import { useElementHeight } from '@/hooks/useElementHeight';
import { useWindowHeight } from '@/hooks/useWindowHeight';
import {
  Blueprint,
  BlueprintPortal,
  useBlueprint,
} from '@/layouts/components/Blueprint';

import { VanillaMobileNavigation } from './components/VanillaMobileNavigation';
import { VanillaNavigation } from './components/VanillaNavigation';

const scrollbar = {
  '::-webkit-scrollbar': {
    display: 'none',
  },
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
};

const VanillaContext = React.createContext<{ footerHeight: number }>({
  footerHeight: 0,
});

const useVanilla = () => React.useContext(VanillaContext);

const VanillaNav: React.FC = (props) => {
  const windowHeight = useWindowHeight();
  const { headerHeight } = useBlueprint();

  return (
    <Box
      alignSelf="flex-start"
      as="nav"
      display={{ base: 'none', lg: 'block' }}
      h={`${windowHeight - headerHeight}px`}
      mx="-1"
      overflowY="auto"
      position="sticky"
      px="1"
      py="4"
      top={`${headerHeight}px`}
      w={{ base: 60, xl: 72 }}
      sx={scrollbar}
    >
      {props.children}
    </Box>
  );
};

export const VanillaMain: React.FC<{ isFullWidth?: boolean }> = (props) => {
  return (
    <Box as="main" py="4" px="1" mx="-1" flex="1" overflowY="auto">
      {props.children}
    </Box>
  );
};

export const VanillaAside: React.FC = (props) => {
  const windowHeight = useWindowHeight();
  const { headerHeight } = useBlueprint();
  const { footerHeight } = useVanilla();

  const height = useBreakpointValue({
    base: windowHeight - headerHeight - footerHeight,
    lg: windowHeight - headerHeight,
  });

  return (
    <Box
      alignSelf="flex-start"
      as="aside"
      display={{ base: 'none', md: 'block' }}
      h={`${height}px`}
      mx="-1"
      overflowY="auto"
      position="sticky"
      px="1"
      py="4"
      top={`${headerHeight}px`}
      w={{ base: 72, lg: 60, xl: 72 }}
      sx={scrollbar}
    >
      {props.children}
    </Box>
  );
};

export const VanillaPortal = BlueprintPortal;

export const Vanilla: React.FC = (props) => {
  const footer = useElementHeight();

  return (
    <VanillaContext.Provider value={{ footerHeight: footer.height }}>
      <Blueprint>
        <Box
          display="flex"
          gap={{ base: 2, md: 4 }}
          maxW="container.xl"
          mx="auto"
          px="4"
          mb={`${footer.height}px`}
        >
          <VanillaNav>
            <VanillaNavigation />
          </VanillaNav>
          {props.children}
        </Box>

        <Box
          display={{ base: 'block', lg: 'none' }}
          as="nav"
          position="fixed"
          bottom="0"
          left="0"
          right="0"
          ref={footer.ref}
          zIndex="docked"
        >
          <VanillaMobileNavigation />
        </Box>
      </Blueprint>
    </VanillaContext.Provider>
  );
};
